import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Globe2, Copy, Check, RefreshCw, Star, Trash2, Plus } from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  GET_TINYCMS_DOMAINS,
  ADD_TINYCMS_DOMAIN,
  CHECK_TINYCMS_DOMAIN,
  SET_PRIMARY_TINYCMS_DOMAIN,
  REMOVE_TINYCMS_DOMAIN,
} from '../../graphql';
import { useAccount } from '../../context/AccountContext';

/**
 * Settings → Domains. Where the public site lives: its free address
 * ({slug}.tinycms.ai) and any custom domains. Connecting a domain is two DNS
 * records — a CNAME that routes it, a TXT that proves it is yours — then the
 * certificate is issued automatically. The first domain to go live becomes
 * primary; every other address redirects there.
 *
 * Changing domains is admin-only (the server enforces it).
 */

interface DnsRecord {
  type: string;
  name: string;
  value: string;
  ok: boolean | null;
}
interface Domain {
  id: string;
  hostname: string;
  status: 'pending' | 'verifying' | 'active' | 'failed';
  isPrimary: boolean;
  lastError?: string | null;
  lastCheckedAt?: string | null;
  certificate?: string | null;
  records: DnsRecord[];
}

const STATUS: Record<
  Domain['status'],
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  pending: { label: 'Waiting for DNS', variant: 'outline' },
  verifying: { label: 'Issuing certificate', variant: 'secondary' },
  active: { label: 'Live', variant: 'default' },
  failed: { label: 'Needs attention', variant: 'destructive' },
};

const CopyButton: React.FC<{ value: string }> = ({ value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title="Copy"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard unavailable */
        }
      }}
      className="ml-1 inline-flex text-cmp-ink-3 hover:text-cmp-ink"
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </button>
  );
};

export function DomainsSettings({ embedded = false }: { embedded?: boolean } = {}) {
  const { accountId } = useAccount();
  const [hostname, setHostname] = useState('');
  const [error, setError] = useState('');

  const { data, loading, refetch } = useQuery(GET_TINYCMS_DOMAINS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const info = data?.tinycmsDomains;
  const domains: Domain[] = info?.domains || [];
  const canManage = Boolean(info?.canManage);

  const onError = (err: Error) => setError(err.message);
  const refresh = () => refetch();
  const [add, { loading: adding }] = useMutation(ADD_TINYCMS_DOMAIN, {
    onError,
    onCompleted: refresh,
  });
  const [check, { loading: checking }] = useMutation(CHECK_TINYCMS_DOMAIN, {
    onError,
    onCompleted: refresh,
  });
  const [setPrimary] = useMutation(SET_PRIMARY_TINYCMS_DOMAIN, { onError, onCompleted: refresh });
  const [remove] = useMutation(REMOVE_TINYCMS_DOMAIN, { onError, onCompleted: refresh });

  if (loading && !info) return <div className="p-6 text-cmp-ink-2">Loading…</div>;

  const primary = domains.find((d) => d.isPrimary && d.status === 'active');

  if (embedded) {
    return (
      <div className="ink-site-card">
        <div className="ink-site-domain-head">
          <span>{info?.freeAddress || 'Set a handle first'}</span>
          {info?.freeAddress ? (
            <span className="ink-site-pill">{primary ? 'Redirects' : 'Primary · free'}</span>
          ) : null}
          {info?.freeAddress ? (
            <button
              type="button"
              className="ink-site-link"
              style={{ marginLeft: 'auto' }}
              onClick={() => navigator.clipboard.writeText(info.freeAddress)}
            >
              Copy
            </button>
          ) : null}
        </div>
        {canManage ? (
          <form
            className="ink-site-connect"
            onSubmit={(e) => {
              e.preventDefault();
              setError('');
              if (hostname.trim()) {
                add({ variables: { accountId, hostname: hostname.trim() } });
                setHostname('');
              }
            }}
          >
            <input
              className="ink-site-input"
              value={hostname}
              onChange={(e) => setHostname(e.target.value)}
              placeholder="blog.yourdomain.com"
            />
            <button
              type="submit"
              className="ink-btn ink-btn-primary"
              disabled={adding || !hostname.trim()}
            >
              Connect
            </button>
          </form>
        ) : (
          <p className="ink-site-hint">Only an account admin can change domains.</p>
        )}
        <div className="ink-site-hint">
          Use a subdomain such as blog.yourdomain.com or www.yourdomain.com.
        </div>
        {error ? (
          <p className="ink-site-hint" style={{ color: 'var(--ink-red)' }}>
            {error}
          </p>
        ) : null}
        {domains.map((d) => (
          <div key={d.id} className="ink-site-row">
            <div>
              <div className="ink-site-row-title" style={{ fontFamily: 'var(--ink-mono)' }}>
                {d.hostname}
              </div>
              <div className="ink-site-row-desc">
                {STATUS[d.status].label}
                {d.isPrimary ? ' · Primary' : ''}
                {d.lastError ? ` · ${d.lastError}` : ''}
              </div>
              {d.status !== 'active' && d.records.length ? (
                <>
                  {d.records.map((r) => (
                    <div
                      key={r.type}
                      className="ink-site-row-desc"
                      style={{ fontFamily: 'var(--ink-mono)', overflowWrap: 'anywhere' }}
                    >
                      {r.ok === true ? '✓ ' : ''}
                      {r.type} {r.name} → {r.value}
                      <CopyButton value={r.value} />
                    </div>
                  ))}
                  <div className="ink-site-row-desc">
                    Using Cloudflare? Set the CNAME to DNS only (grey cloud), not Proxied, and paste
                    each value exactly as shown — the TXT value includes tinycms-verify=.
                  </div>
                </>
              ) : null}
            </div>
            {canManage ? (
              <span style={{ display: 'flex', gap: 8 }}>
                {d.status !== 'active' ? (
                  <button
                    type="button"
                    className="ink-site-link"
                    disabled={checking}
                    onClick={() => check({ variables: { accountId, id: d.id } })}
                  >
                    Check
                  </button>
                ) : null}
                <button
                  type="button"
                  className="ink-site-link"
                  style={{ color: 'var(--ink-red)' }}
                  onClick={() => {
                    if (window.confirm(`Disconnect ${d.hostname}? It stops serving your site.`)) {
                      remove({ variables: { accountId, id: d.id } });
                    }
                  }}
                >
                  Remove
                </button>
              </span>
            ) : null}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold text-cmp-ink flex items-center gap-2">
          <Globe2 className="w-5 h-5" /> Domains
        </h2>
        <p className="text-sm text-cmp-ink-2 mt-1">
          Where your public site lives. Connect your own domain with two DNS records; the secure
          certificate is set up for you.
        </p>
      </div>

      <Card className="p-4 bg-cmp-raise border border-cmp-line text-cmp-ink space-y-1">
        <p className="text-sm font-medium">Free address</p>
        {info?.freeAddress ? (
          <p className="text-sm">
            <span className="font-mono">{info.freeAddress}</span>
            {primary ? (
              <span className="text-cmp-ink-2"> — redirects to {primary.hostname}</span>
            ) : (
              <span className="text-cmp-ink-2"> — your site’s main address</span>
            )}
          </p>
        ) : (
          <p className="text-sm text-cmp-ink-2">Set a handle in Settings → Public Site first.</p>
        )}
      </Card>

      {canManage ? (
        <Card className="p-4 bg-cmp-raise border border-cmp-line text-cmp-ink">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setError('');
              if (hostname.trim()) {
                add({ variables: { accountId, hostname: hostname.trim() } });
                setHostname('');
              }
            }}
            className="flex flex-wrap gap-2"
          >
            <input
              value={hostname}
              onChange={(e) => setHostname(e.target.value)}
              placeholder="blog.yourdomain.com"
              className="flex-1 min-w-[16rem] px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2"
            />
            <Button type="submit" disabled={adding || !hostname.trim()}>
              <Plus /> Connect domain
            </Button>
          </form>
          <p className="mt-2 text-xs text-cmp-ink-3">
            Use a subdomain such as blog.yourdomain.com or www.yourdomain.com.
          </p>
        </Card>
      ) : (
        <p className="text-sm text-cmp-ink-2">Only an account admin can change domains.</p>
      )}

      {error ? <p className="text-sm text-error">{error}</p> : null}
      {info && !info.edgeConfigured && domains.length ? (
        <p className="text-sm text-warning">
          Domains can be verified now; they go live once secure certificates are switched on for the
          platform.
        </p>
      ) : null}

      {domains.map((d) => (
        <Card key={d.id} className="p-4 bg-cmp-raise border border-cmp-line text-cmp-ink space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono">{d.hostname}</span>
              <Badge variant={STATUS[d.status].variant}>{STATUS[d.status].label}</Badge>
              {d.isPrimary ? <Badge variant="secondary">Primary</Badge> : null}
            </div>
            {canManage ? (
              <div className="flex flex-wrap gap-2">
                {d.status !== 'active' ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={checking}
                    onClick={() => {
                      setError('');
                      check({ variables: { accountId, id: d.id } });
                    }}
                  >
                    <RefreshCw /> Check now
                  </Button>
                ) : null}
                {d.status === 'active' && !d.isPrimary ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPrimary({ variables: { accountId, id: d.id } })}
                  >
                    <Star /> Make primary
                  </Button>
                ) : null}
                {d.isPrimary ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setPrimary({ variables: { accountId, id: null } })}
                  >
                    Use free address instead
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (window.confirm(`Disconnect ${d.hostname}? It stops serving your site.`)) {
                      remove({ variables: { accountId, id: d.id } });
                    }
                  }}
                >
                  <Trash2 /> Remove
                </Button>
              </div>
            ) : null}
          </div>

          {d.status !== 'active' ? (
            <div>
              <p className="text-sm text-cmp-ink-2 mb-2">
                Add these records at your DNS provider, then press Check now:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-cmp-ink-3">
                      <th className="py-1 pr-3">Type</th>
                      <th className="py-1 pr-3">Name</th>
                      <th className="py-1 pr-3">Value</th>
                      <th className="py-1">Found</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.records.map((r) => (
                      <tr key={r.type} className="border-t border-cmp-line">
                        <td className="py-2 pr-3 font-mono">{r.type}</td>
                        <td className="py-2 pr-3 font-mono break-all">
                          {r.name}
                          <CopyButton value={r.name} />
                        </td>
                        <td className="py-2 pr-3 font-mono break-all">
                          {r.value}
                          <CopyButton value={r.value} />
                        </td>
                        <td className="py-2">
                          {r.ok === true ? (
                            <Check className="size-4 text-success" />
                          ) : r.ok === false ? (
                            '—'
                          ) : (
                            ''
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-xs text-cmp-ink-3">
                Using Cloudflare? Set the CNAME to DNS only (grey cloud), not Proxied, and paste
                each value exactly as shown — the TXT value includes tinycms-verify=.
              </p>
              {d.lastError ? <p className="mt-2 text-xs text-cmp-ink-2">{d.lastError}</p> : null}
            </div>
          ) : null}
        </Card>
      ))}
    </div>
  );
}
